import { GenieAvatar } from "@/components/GenieAvatar";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import type { Agent } from "@/types/agent/agent";

interface GenieMiniAvatarProps {
  agent: Agent;
  size?: number;
}

// Lightweight per-Genie circle. Mirrors the pattern used by the brain
// GenieScopeSelector so the chrome stays consistent across the app.
export const GenieMiniAvatar = ({ agent, size = 24 }: GenieMiniAvatarProps) => (
  <span
    className="inline-flex items-center justify-center overflow-hidden rounded-full ring-2 ring-white bg-white"
    style={{ width: size, height: size }}
  >
    <GenieAvatar
      size={size}
      color={agent.branding?.primaryColor ?? DEFAULT_BRANDING.primaryColor}
      secondaryColor={agent.branding?.secondaryColor ?? null}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
      avatarBackgroundOpacity={agent.branding?.avatarBackgroundOpacity}
      customAvatarUrl={agent.branding?.logoUrl || agent.branding?.customAvatarUrl || null}
    />
  </span>
);

interface GenieAttributionProps {
  agentIds: string[] | null | undefined;
  agents: Agent[];
  label?: string;
  size?: number;
  className?: string;
}

const titleFromIds = (ids: string[] | null | undefined, agents: Agent[]): string => {
  if (!ids || ids.length === 0) return "All of your Genies";
  const names = ids.map((id) => agents.find((a) => a.id === id)?.name).filter(Boolean) as string[];
  if (names.length === 0) return "All of your Genies";
  return names.join(", ");
};

// Renders the Genies a view is scoped to. Used in the view list cards and the
// view detail header so customers always see *which* Genies a view analyses.
export const GenieAttribution = ({
  agentIds,
  agents,
  label = "Analyzing",
  size = 22,
  className = "",
}: GenieAttributionProps) => {
  const hasIds = !!agentIds && agentIds.length > 0;
  const resolved = hasIds ? (agentIds!.map((id) => agents.find((a) => a.id === id)).filter(Boolean) as Agent[]) : [];
  const isAll = !hasIds || resolved.length === 0;
  const display = resolved.slice(0, 4);
  const extra = resolved.length - display.length;
  const tooltip = titleFromIds(agentIds, agents);

  return (
    <div className={`inline-flex items-center gap-2 ${className}`} title={tooltip}>
      <span className="text-[10px] uppercase tracking-wider font-semibold text-[#64748B]">{label}:</span>
      {isAll ? (
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-flex items-center justify-center rounded-full text-[9px] font-bold text-white"
            style={{ width: size, height: size, background: "linear-gradient(135deg, #4E9CFF, #FF4DB8)" }}
            aria-hidden
          >
            All
          </span>
          <span className="text-xs font-semibold text-[#222B45]">All of your Genies</span>
        </span>
      ) : (
        <span className="inline-flex items-center">
          <span className="inline-flex items-center">
            {display.map((a, i) => (
              <span key={a.id} style={{ marginLeft: i === 0 ? 0 : -8, zIndex: display.length - i }}>
                <GenieMiniAvatar agent={a} size={size} />
              </span>
            ))}
            {extra > 0 && (
              <span
                className="inline-flex items-center justify-center rounded-full bg-[#FAFCFF] border border-[#E5EAF0] text-[10px] font-semibold text-[#64748B] ring-2 ring-white"
                style={{ width: size, height: size, marginLeft: -8, zIndex: 0 }}
              >
                +{extra}
              </span>
            )}
          </span>
          <span className="ml-2 text-xs font-semibold text-[#222B45] truncate max-w-[280px]" title={tooltip}>
            {resolved.length === 1
              ? resolved[0].name
              : `${resolved[0].name} +${resolved.length - 1} more`}
          </span>
        </span>
      )}
    </div>
  );
};
